/**
 * Request Queue Utility
 * 
 * Manages concurrent HTTP requests to prevent overwhelming the server's HTTP/2 connection pool.
 * Implements priority-based queuing and ensures maximum concurrent request limits.
 * 
 * @example
 * const queue = RequestQueue.getInstance();
 * const result = await queue.enqueue(() => api.get('/trainings'), 'high');
 */

export enum RequestPriority {
    HIGH = 3,
    NORMAL = 2,
    LOW = 1
}

interface QueuedRequest<T> {
    id: string;
    execute: () => Promise<T>;
    priority: RequestPriority;
    resolve: (value: T) => void;
    reject: (error: any) => void;
    timestamp: number;
}

export class RequestQueue {
    private static instance: RequestQueue;
    private queue: QueuedRequest<any>[] = [];
    private activeRequests: Set<string> = new Set();
    private readonly maxConcurrent: number = 3;
    private requestIdCounter: number = 0;

    private constructor() { }

    public static getInstance(): RequestQueue {
        if (!RequestQueue.instance) {
            RequestQueue.instance = new RequestQueue();
        }
        return RequestQueue.instance;
    }

    /**
     * Enqueue a request with specified priority
     * @param executor Function that returns a Promise
     * @param priority Request priority (high/normal/low)
     * @returns Promise that resolves with the request result
     */
    public enqueue<T>(
        executor: () => Promise<T>,
        priority: RequestPriority = RequestPriority.NORMAL
    ): Promise<T> {
        return new Promise((resolve, reject) => {
            const id = `req_${++this.requestIdCounter}_${Date.now()}`;
            const request: QueuedRequest<T> = {
                id,
                execute: executor,
                priority,
                resolve,
                reject,
                timestamp: Date.now()
            };

            // Insert into queue based on priority
            this.insertByPriority(request);

            // Try to process immediately if we have capacity
            this.processQueue();
        });
    }

    /**
     * Insert request into queue maintaining priority order
     */
    private insertByPriority(request: QueuedRequest<any>): void {
        let inserted = false;
        for (let i = 0; i < this.queue.length; i++) {
            if (request.priority > this.queue[i].priority) {
                this.queue.splice(i, 0, request);
                inserted = true;
                break;
            }
        }
        if (!inserted) {
            this.queue.push(request);
        }
    }

    /**
     * Process queued requests up to max concurrent limit
     */
    private async processQueue(): Promise<void> {
        while (
            this.queue.length > 0 &&
            this.activeRequests.size < this.maxConcurrent
        ) {
            const request = this.queue.shift();
            if (!request) break;

            this.activeRequests.add(request.id);

            try {
                const result = await request.execute();
                request.resolve(result);
            } catch (error) {
                request.reject(error);
            } finally {
                this.activeRequests.delete(request.id);
                // Continue processing queue after this request completes
                this.processQueue();
            }
        }
    }

    /**
     * Get current queue status for debugging
     */
    public getStatus(): {
        queuedCount: number;
        activeCount: number;
        capacity: number;
    } {
        return {
            queuedCount: this.queue.length,
            activeCount: this.activeRequests.size,
            capacity: this.maxConcurrent
        };
    }

    /**
     * Clear all queued requests (use with caution)
     */
    public clear(): void {
        this.queue.forEach(req =>
            req.reject(new Error('Request queue cleared'))
        );
        this.queue = [];
    }
}

export default RequestQueue.getInstance();
